import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { getStates } from '../api/leads-api';
import type { LeadFormData } from '../types';

interface AddressSectionProps {
  formData: LeadFormData;
  onChange: (field: keyof LeadFormData, value: string | number | boolean | undefined) => void;
}

export function AddressSection({ formData, onChange }: AddressSectionProps) {
  const states = getStates();

  return (
    <div className="grid grid-cols-4 gap-2">
      {/* Property Address */}
      <div>
        <h4 className="text-[#0A0A9E] font-semibold text-xs mb-1">Property Address</h4>
        <div className="space-y-0.5">
          <div>
            <Label htmlFor="address1" className="text-[10px]">Address 1 *</Label>
            <Input
              id="address1"
              value={formData.address1}
              onChange={(e) => onChange('address1', e.target.value)}
              required
              className="h-6 text-xs"
            />
          </div>
          <div>
            <Label htmlFor="address2" className="text-[10px]">Address 2</Label>
            <Input
              id="address2"
              value={formData.address2 || ''}
              onChange={(e) => onChange('address2', e.target.value)}
              className="h-6 text-xs"
            />
          </div>
          <div>
            <Label htmlFor="city" className="text-[10px]">City *</Label>
            <Input
              id="city"
              value={formData.city}
              onChange={(e) => onChange('city', e.target.value)}
              required
              className="h-6 text-xs"
            />
          </div>
          <div className="grid grid-cols-2 gap-1">
            <div>
              <Label htmlFor="state" className="text-[10px]">State *</Label>
              <Input
                id="state"
                value="OH"
                disabled
                className="bg-gray-50 h-6 text-xs"
              />
            </div>
            <div>
              <Label htmlFor="zip" className="text-[10px]">Zip *</Label>
              <Input
                id="zip"
                value={formData.zip}
                onChange={(e) => onChange('zip', e.target.value)}
                maxLength={10}
                required
                className="h-6 text-xs"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Billing Address */}
      <div>
        <h4 className="text-[#0A0A9E] font-semibold text-xs mb-1">Billing Address</h4>
        {!formData.hasSameAddress ? (
          <div className="space-y-0.5">
            <div>
              <Label htmlFor="billingAddress1" className="text-[10px]">Address 1</Label>
              <Input
                id="billingAddress1"
                value={formData.billingAddress1 || ''}
                onChange={(e) => onChange('billingAddress1', e.target.value)}
                className="h-6 text-xs"
              />
            </div>
            <div>
              <Label htmlFor="billingAddress2" className="text-[10px]">Address 2</Label>
              <Input
                id="billingAddress2"
                value={formData.billingAddress2 || ''}
                onChange={(e) => onChange('billingAddress2', e.target.value)}
                className="h-6 text-xs"
              />
            </div>
            <div>
              <Label htmlFor="billingCity" className="text-[10px]">City</Label>
              <Input
                id="billingCity"
                value={formData.billingCity || ''}
                onChange={(e) => onChange('billingCity', e.target.value)}
                className="h-6 text-xs"
              />
            </div>
            <div className="grid grid-cols-2 gap-1">
              <div>
                <Label htmlFor="billingState" className="text-[10px]">State</Label>
                <Select
                  id="billingState"
                  value={formData.billingState || ''}
                  onChange={(e) => onChange('billingState', e.target.value)}
                  className="h-6 text-xs"
                >
                  <option value="">-</option>
                  {states.map((state) => (
                    <option key={state.code} value={state.code}>
                      {state.code}
                    </option>
                  ))}
                </Select>
              </div>
              <div>
                <Label htmlFor="billingZip" className="text-[10px]">Zip</Label>
                <Input
                  id="billingZip"
                  value={formData.billingZip || ''}
                  onChange={(e) => onChange('billingZip', e.target.value)}
                  maxLength={10}
                  className="h-6 text-xs"
                />
              </div>
            </div>
          </div>
        ) : (
          <div className="text-[10px] text-gray-500 italic py-1">Same as property address</div>
        )}
      </div>

      {/* Tenant Information */}
      <div>
        <h4 className="text-[#0A0A9E] font-semibold text-xs mb-1">Tenant Information</h4>
        <div className="space-y-0.5">
          <div>
            <Label htmlFor="tenantName" className="text-[10px]">Tenant Name</Label>
            <Input
              id="tenantName"
              value={formData.tenantName || ''}
              onChange={(e) => onChange('tenantName', e.target.value)}
              className="h-6 text-xs"
            />
          </div>
          <div>
            <Label htmlFor="tenantHomePhone" className="text-[10px]">Home Phone</Label>
            <Input
              id="tenantHomePhone"
              type="tel"
              value={formData.tenantHomePhone || ''}
              onChange={(e) => onChange('tenantHomePhone', e.target.value)}
              className="h-6 text-xs"
            />
          </div>
          <div>
            <Label htmlFor="tenantCellPhone" className="text-[10px]">Cell Phone</Label>
            <Input
              id="tenantCellPhone"
              type="tel"
              value={formData.tenantCellPhone || ''}
              onChange={(e) => onChange('tenantCellPhone', e.target.value)}
              className="h-6 text-xs"
            />
          </div>
          <div>
            <Label htmlFor="tenantWorkPhone" className="text-[10px]">Work Phone</Label>
            <Input
              id="tenantWorkPhone"
              type="tel"
              value={formData.tenantWorkPhone || ''}
              onChange={(e) => onChange('tenantWorkPhone', e.target.value)}
              className="h-6 text-xs"
            />
          </div>
          <div>
            <Label htmlFor="tenantOtherPhone" className="text-[10px]">Other Phone</Label>
            <Input
              id="tenantOtherPhone"
              type="tel"
              value={formData.tenantOtherPhone || ''}
              onChange={(e) => onChange('tenantOtherPhone', e.target.value)}
              className="h-6 text-xs"
            />
          </div>
        </div>
      </div>

      {/* Contact Information */}
      <div>
        <h4 className="text-[#0A0A9E] font-semibold text-xs mb-1">Contact Information</h4>
        <div className="space-y-0.5">
          <div>
            <Label htmlFor="email" className="text-[10px]">Email</Label>
            <Input
              id="email"
              type="email"
              value={formData.email || ''}
              onChange={(e) => onChange('email', e.target.value)}
              placeholder="customer@example.com"
              className="h-6 text-xs"
            />
          </div>
          <div>
            <Label htmlFor="homePhone" className="text-[10px]">Home Phone</Label>
            <Input
              id="homePhone"
              type="tel"
              value={formData.homePhone || ''}
              onChange={(e) => onChange('homePhone', e.target.value)}
              placeholder="(555) 123-4567"
              className="h-6 text-xs"
            />
          </div>
          <div>
            <Label htmlFor="cellPhone" className="text-[10px]">Cell Phone</Label>
            <Input
              id="cellPhone"
              type="tel"
              value={formData.cellPhone || ''}
              onChange={(e) => onChange('cellPhone', e.target.value)}
              placeholder="(555) 123-4567"
              className="h-6 text-xs"
            />
          </div>
          <div>
            <Label htmlFor="workPhone" className="text-[10px]">Work Phone</Label>
            <Input
              id="workPhone"
              type="tel"
              value={formData.workPhone || ''}
              onChange={(e) => onChange('workPhone', e.target.value)}
              placeholder="(555) 123-4567"
              className="h-6 text-xs"
            />
          </div>
          <div>
            <Label htmlFor="otherPhone" className="text-[10px]">Other Phone</Label>
            <Input
              id="otherPhone"
              type="tel"
              value={formData.otherPhone || ''}
              onChange={(e) => onChange('otherPhone', e.target.value)}
              placeholder="(555) 123-4567"
              className="h-6 text-xs"
            />
          </div>
        </div>
      </div>
    </div>
  );
}
